import type { UseMutationResult } from "@tanstack/react-query";
import type { EquipmentTreeRowFlat } from "../models";

/**
 * Centralized handler for Set Online / Set Offline operations.
 *
 * All 3 screens (Roof, Sidewall, Caster Mold) follow the same pattern:
 * 1. Find the current occupant on the target location (if any)
 * 2. If occupant exists, call setOffline for it (atomic swap at same timestamp)
 * 3. Call setOnline for the new part
 *
 * The only differences are:
 * - How to find the occupant (predicate varies per screen)
 * - The mutation parameter shapes (roofPartId vs moldPartId+strandPartId, etc.)
 *
 * This hook extracts the common orchestration logic. Each page provides:
 * - findOccupant: function to find the current occupant in tree data
 * - buildOnlineParams / buildOfflineParams: functions to construct mutation params
 * - setOnlineMutation / setOfflineMutation: the actual mutations
 */

interface UseSetEquipmentOnlineOfflineProps<
  TRow extends EquipmentTreeRowFlat,
  TOnlineParams,
  TOfflineParams,
> {
  /** Mutation for setting equipment online */
  setOnlineMutation: UseMutationResult<unknown, unknown, TOnlineParams>;
  /** Mutation for setting equipment offline */
  setOfflineMutation: UseMutationResult<unknown, unknown, TOfflineParams>;
  /**
   * Find the current occupant that would be displaced.
   * Return null if no occupant (empty position).
   */
  findOccupant: (treeData: TRow[], targetRow: TRow) => TRow | null;
  /**
   * Build the params for the setOnline mutation.
   * @param targetRow - The equipment being set online
   * @param timestamp - The timestamp for the operation
   */
  buildOnlineParams: (targetRow: TRow, timestamp: Date | null) => TOnlineParams;
  /**
   * Build the params for the setOffline mutation (for the occupant being displaced).
   * @param occupant - The occupant being set offline
   * @param timestamp - The same timestamp as the online operation
   */
  buildOfflineParams: (
    occupant: TRow,
    timestamp: Date | null,
  ) => TOfflineParams;
}

interface SetOnlineOfflineResult<TRow extends EquipmentTreeRowFlat> {
  /**
   * Execute the Set Online operation (with optional swap).
   * @param treeData - Current tree data (to find occupant)
   * @param targetRow - The equipment to set online
   * @param timestamp - The timestamp for the operation
   * @returns The occupant that was displaced (if any), or null
   */
  handleSetOnline: (
    treeData: TRow[],
    targetRow: TRow,
    timestamp: Date | null,
  ) => Promise<TRow | null>;
  /**
   * Execute the Set Offline operation.
   * @param targetRow - The equipment to set offline
   * @param timestamp - The timestamp for the operation
   */
  handleSetOffline: (targetRow: TRow, timestamp: Date | null) => Promise<void>;
  /** Whether any mutation is currently pending */
  isPending: boolean;
}

export const useSetEquipmentOnlineOffline = <
  TRow extends EquipmentTreeRowFlat,
  TOnlineParams,
  TOfflineParams,
>({
  setOnlineMutation,
  setOfflineMutation,
  findOccupant,
  buildOnlineParams,
  buildOfflineParams,
}: UseSetEquipmentOnlineOfflineProps<
  TRow,
  TOnlineParams,
  TOfflineParams
>): SetOnlineOfflineResult<TRow> => {
  const handleSetOnline = async (
    treeData: TRow[],
    targetRow: TRow,
    timestamp: Date | null,
  ): Promise<TRow | null> => {
    // Step 1: Find occupant
    const occupant = findOccupant(treeData, targetRow);

    // Step 2: If occupant, set it offline first.
    // The online must be strictly later than the offline (location-history
    // constraint in UpsertTask), so bump the online by 1 second when swapping.
    // The date picker is minute-resolution, so this is invisible to the user.
    let onlineTimestamp = timestamp;
    if (occupant) {
      await setOfflineMutation.mutateAsync(
        buildOfflineParams(occupant, timestamp),
      );
      if (timestamp) {
        onlineTimestamp = new Date(timestamp.getTime() + 1000);
      }
    }

    // Step 3: Set the target online
    await setOnlineMutation.mutateAsync(
      buildOnlineParams(targetRow, onlineTimestamp),
    );

    return occupant;
  };

  const handleSetOffline = async (
    targetRow: TRow,
    timestamp: Date | null,
  ): Promise<void> => {
    await setOfflineMutation.mutateAsync(
      buildOfflineParams(targetRow, timestamp),
    );
  };

  const isPending = setOnlineMutation.isPending || setOfflineMutation.isPending;

  return { handleSetOnline, handleSetOffline, isPending };
};
